import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';

const TCategory = () => {
  const location = useLocation();
  const { userData } = useUser();
  const { categoryId } = location.state || {};
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const refreshData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(process.env.REACT_APP_API_TICKET_CATEGORY_LIST, {
        headers: {
          'Content-Type': 'application/json',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        }
      });
      
      const categoryList = response.data?.data?.categories || [];
      
      const formatted = categoryList.map((cat) => ({
        id: cat.id,
        name: cat.name,
        description: cat.description || '',
        status: cat.statuses.length > 0 ? cat.statuses.map(status => status.name).join(', ') : 'No Statuses',
      }));
  
      setCategories(formatted);
  
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    refreshData();
  }, []);

  const handleEdit = (event, categoryId) => {
    const url = `/CategoryCreation?categoryId=${categoryId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };
  
  const handleSubCategory = (event, categoryId) => {
    const url = `/ticketSubCategory?categoryId=${categoryId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };
  
  const handleDelete = (categoryId) => {
    Swal.fire({
      title: 'Delete this category?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          console.log('Calling delete API with id:', categoryId);
  

          const response = await axios.post(
            `${process.env.REACT_APP_API_DELETE_CATEGORY}?ids=${categoryId}`,
            {},
            {
              headers: {
                'Content-Type': 'application/json',
                'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
              },
            }
          );
  
          console.log('Delete API response:', response.data);
  
          if (response.data.success) {
            setCategories((prev) => prev.filter((c) => c.id !== categoryId));
            Swal.fire({
              icon: 'success',
              title: 'Deleted Successfully!',
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
            });
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Error!',
              text: response.data.success_message || 'Failed to delete.', 
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
            });
          }          
        } catch (err) {
          console.error('Delete API error:', err.response || err.message);
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: err.response?.data?.message || 'Something went wrong.',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
          });
        }
      }
    });
  };
  
  const handleNewCategory = (event) => {
    const url = `/CategoryCreation`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };
  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const sortedCategories = [...categories].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let aValue = a[sortConfig.key], bValue = b[sortConfig.key];
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    return (aValue < bValue ? -1 : 1) * (sortConfig.direction === 'asc' ? 1 : -1);
  });

  const filtered = sortedCategories.filter((c) =>
    Object.values(c)
      .flatMap(v => Array.isArray(v) ? v : [v])
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const totalEntries = filtered.length;
  const indexOfFirstRow = (currentPage - 1) * rowsPerPage;
  const currentRows = filtered.slice(indexOfFirstRow, indexOfFirstRow + rowsPerPage);
  const totalPages = Math.ceil(totalEntries / rowsPerPage);
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  const handlePageClick = (page) => setCurrentPage(page);
  const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage((prev) => prev + 1); };
  const handlePrevPage = () => { if (currentPage > 1) setCurrentPage((prev) => prev - 1); };
  const handleItemsPerPageChange = (e) => {
    setrowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () =>
    generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">{page}</span>
      )
    );

  return (
    <div className="enrollment-container">
      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">🔍 Search</label>
        </div>
        {hasPrivilege(userData, 'TICKET', 'TICKET_CATEGORY_MODIFY') && (
        <button className="create-button-new" onClick={handleNewCategory}>
          <span className="create-button__icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="23" height="23">
              <path d="M12 5v14M5 12h14" stroke="white" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="create-button__label">Add Category</span>
        </button>
        )}
      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
        <option value={100}>100 items per page</option>
      </select>
      <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={refreshData}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>
      
      <div className="tt-table-container">
        {loading ? (
          <div className="tt-loading"><div className="tt-loading-spinner"></div><p>Loading Categories...</p></div>
        ) : (
          <table className="tt-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')}>
                  <div className="th-content">
                    <span>Category Name</span>
                    <FiChevronDown className={`sort-icon ${sortConfig.key === 'name' ? 'active' : ''} ${sortConfig.direction === 'desc' ? 'desc' : ''}`} />
                  </div>
                </th>
                <th onClick={() => handleSort('status')}>
                  <div className="th-content">
                    <span>Status</span>
                    <FiChevronDown
                      className={`sort-icon \${sortConfig.key === 'status' ? 'active' : ''} \${sortConfig.direction === 'desc' ? 'desc' : ''}`}
                    />
                  </div>
                </th>
                <th><div className="th-content"><span>Description</span></div></th>
                {hasPrivilege(userData, 'TICKET', 'TICKET_CATEGORY_MODIFY') && (
                <th>Action</th>
                )}
              </tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? currentRows.map(c => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.status}</td>
                  <td>{c.description}</td>
                  {hasPrivilege(userData, 'TICKET', 'TICKET_CATEGORY_MODIFY') && (
                  <td className="action-buttons">
                    <button className="edit-btn tooltip-btn" onClick={(e) => handleEdit(e, c.id)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.12 2.12 0 0 1 3.001 3.001L7.5 18.85l-4 1 1-4L16.862 3.487z" />
                      </svg>
                      <span className="tooltip-text">Edit</span>
                    </button>
                    <button className="delete-btn tooltip-btn" onClick={() => handleDelete(c.id)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                      </svg>
                      <span className="tooltip-text">Delete</span>
                    </button>
                    <button className="drill-btn tooltip-btn" onClick={(e) => handleSubCategory(e, c.id)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" id="categories">
                        <path fill="none" d="M0 0h24v24H0Z" />
                        <path fill="#FFFFFF" d="M15.278 20.72a3.086 3.086 0 0 1-.621-.9H8.409a2.706 2.706 0 0 1-2.7-2.7V8.267a3 3 0 0 1 .895-5.865 3 3 0 0 1 .908 5.859v1.951h7.025a2.99 2.99 0 1 1 .006 1.8H7.512v5.106a.9.9 0 0 0 .9.9h6.047a3 3 0 1 1 .822 2.7Zm.922-2.121a1.2 1.2 0 1 0 1.2-1.2 1.2 1.2 0 0 0-1.2 1.197Zm0-7.5a1.2 1.2 0 1 0 1.2-1.2 1.2 1.2 0 0 0-1.2 1.197Zm-10.8-5.7a1.2 1.2 0 1 0 1.2-1.2 1.2 1.2 0 0 0-1.2 1.197Z" />
                      </svg>
                      <span className="tooltip-text">Subcategory</span>
                    </button>
                  </td>
                  )}
                </tr>
              )) : (
                <tr><td colSpan="4" style={{ textAlign: "center" }}>No Categories Found</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button className="prev-button" onClick={handlePrevPage} disabled={currentPage === 1}>Previous</button>
        {renderPagination()}
        <button className="next-button" onClick={handleNextPage} disabled={currentPage === totalPages}>Next</button>
      </div>
    </div>
  );
};

export default TCategory;
